import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderOpen,
  Search,
  Trash2,
  Users,
  Eye,
  PenSquare,
  ReceiptText,
  Server,
  RadioTower,
  ExternalLink,
  Copy,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, TOOL_META } from "@/lib/format";
import { Segmented } from "@/components/common/Cards";

const ICONS = { pajak: ReceiptText, hosting: Server, isp: RadioTower };

export default function SavedPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [tool, setTool] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setItems(null);
    api
      .get("/calculations", { params: { scope, tool: tool === "all" ? undefined : tool, q: q || undefined, limit: 200 } })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, tool]);

  useEffect(() => {
    const h = setTimeout(load, 220);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const grouped = useMemo(() => {
    const g = { pajak: [], hosting: [], isp: [] };
    (items || []).forEach((c) => g[c.tool]?.push(c));
    return g;
  }, [items]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/calculations/${confirmDelete.id}`);
      toast.success("Perhitungan dihapus");
      setItems((xs) => xs.filter((c) => c.id !== confirmDelete.id));
    } catch (e) {
      toast.error(errMsg(e, "Gagal menghapus"));
    } finally {
      setConfirmDelete(null);
    }
  };

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/calculations/${c.id}/duplicate`, {});
      toast.success("Perhitungan diduplikat");
      nav(`${TOOL_META[c.tool].path}?id=${data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Gagal menduplikat"));
    }
  };

  const empty = items && items.length === 0;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <FolderOpen className="h-3.5 w-3.5" /> Perhitungan Tersimpan
          </div>
          <h1 className="font-display text-4xl mt-1.5" data-testid="saved-page-title">
            Perhitungan Tersimpan
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">
            Semua perhitungan yang Anda simpan dan yang dibagikan kepada Anda.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px] max-w-[460px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari berdasarkan judul…"
              className="h-10 pl-9 bg-[hsl(220_12%_8%)] border-border"
              data-testid="saved-search-input"
            />
          </div>
          <Segmented
            value={scope}
            onChange={setScope}
            testid="saved-scope"
            size="sm"
            options={[
              { value: "all", label: "Semua" },
              { value: "mine", label: "Milik saya" },
              { value: "shared", label: "Dibagikan ke saya" },
            ]}
          />
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" /> Tool
          </div>
          <Segmented
            value={tool}
            onChange={setTool}
            testid="saved-tool-filter"
            size="sm"
            options={[
              { value: "all", label: "Semua" },
              { value: "pajak", label: "Pajak" },
              { value: "hosting", label: "Hosting" },
              { value: "isp", label: "ISP" },
            ]}
          />
        </div>
      </div>

      {items === null && (
        <div className="space-y-2" data-testid="saved-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 bg-[hsl(220_10%_11%)]" />
          ))}
        </div>
      )}

      {empty && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center" data-testid="saved-empty">
          <FolderOpen className="h-8 w-8 text-muted-foreground mx-auto" />
          <h3 className="font-display text-xl mt-3">Belum ada perhitungan</h3>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Mulai dari salah satu tool. Semua perhitungan yang disimpan akan muncul di sini.
          </p>
          <div className="flex justify-center gap-2 mt-5 flex-wrap">
            {["pajak", "hosting", "isp"].map((k) => (
              <Link
                key={k}
                to={TOOL_META[k].path}
                className="press rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-[hsl(220_10%_10%)]"
                data-testid={`saved-empty-link-${k}`}
              >
                {TOOL_META[k].label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="space-y-6" data-testid="saved-sections">
          {["pajak", "hosting", "isp"].map((k) => {
            if (tool !== "all" && tool !== k) return null;
            const rows = grouped[k] || [];
            if (!rows.length) return null;
            const Icon = ICONS[k];
            return (
              <section key={k} data-testid={`saved-section-${k}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-[hsl(43_60%_62%)]">{TOOL_META[k].label}</h2>
                    <Badge variant="outline" className="border-border text-muted-foreground num text-[10px]">
                      {rows.length}
                    </Badge>
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
                  {rows.map((c) => {
                    const isOwner = c.permission === "owner";
                    return (
                      <div
                        key={c.id}
                        className="flex flex-wrap items-center gap-3 p-4 hover:bg-[hsl(220_10%_9%)] nav-link"
                        data-testid={`saved-row-${c.id}`}
                      >
                        <button
                          className="text-left min-w-0 flex-1"
                          onClick={() => nav(`${TOOL_META[c.tool].path}?id=${c.id}`)}
                          data-testid={`saved-open-${c.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground font-medium truncate max-w-[520px]">{c.title}</span>
                            {c.permission === "view" && (
                              <Badge variant="outline" className="border-[color:var(--hairline)] text-[hsl(43_60%_66%)] text-[10px]">
                                <Eye className="h-3 w-3 mr-1" /> Lihat saja
                              </Badge>
                            )}
                            {c.permission === "edit" && (
                              <Badge variant="outline" className="border-[color:var(--hairline)] text-[hsl(43_60%_66%)] text-[10px]">
                                <PenSquare className="h-3 w-3 mr-1" /> Bisa edit
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[640px]">
                            {c.summary?.headline || "Perhitungan tersimpan"}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                            <span className="num">{fmtDate(c.updated_at)}</span>
                            {!isOwner && <span>· oleh {c.owner_email}</span>}
                            {isOwner && c.shares?.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-[hsl(43_60%_66%)]">
                                · <Users className="h-3 w-3" /> dibagikan ke {c.shares.length}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Buka"
                            onClick={() => nav(`${TOOL_META[c.tool].path}?id=${c.id}`)}
                            data-testid={`saved-open-btn-${c.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Duplikat"
                            onClick={() => duplicate(c)}
                            data-testid={`saved-duplicate-${c.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_62%)]"
                              title="Hapus"
                              onClick={() => setConfirmDelete(c)}
                              data-testid={`saved-delete-${c.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus perhitungan ini?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirmDelete?.title}” akan dihapus permanen termasuk akses bagi orang yang pernah dibagikan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="saved-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(0_60%_42%)] hover:bg-[hsl(0_70%_45%)] text-white" data-testid="saved-delete-confirm">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
